export function nextSortOrder(items) {
    const sortedItems = sortByOrder(items);
    const lastItem = sortedItems[sortedItems.length - 1];
    if (lastItem) {
        return lastItem.sortOrder + 1;
    }
    else {
        return 0;
    }
}
export function previousSortOrder(items) {
    const sortedItems = sortByOrder(items);
    const firstItem = sortedItems[0];
    if (firstItem) {
        return firstItem.sortOrder - 1;
    }
    else {
        return 0;
    }
}
export function targetSortOrder(items, targetSortOrder, relative) {
    return targetSortOrderByFn(items, (i) => i.sortOrder, targetSortOrder, relative);
}
export function targetSortOrderByFn(items, fn, target, relative) {
    const result = adjacentItemForTargetByFn(items, fn, target, relative);
    if (!result) {
        return 0;
    }
    const { adjacentItem, targetItem } = result;
    if (!adjacentItem) {
        return fn(targetItem) + diffForRelative(relative);
    }
    else {
        return (fn(targetItem) + fn(adjacentItem)) / 2;
    }
}
function diffForRelative(relative) {
    return relative === 'after' ? 1 : -1;
}
function adjacentItemForTargetByFn(items, fn, target, relative) {
    const sortedItems = sortByFn(items, fn);
    const targetIndex = sortedItems.findIndex((i) => fn(i) === target);
    const targetItem = sortedItems[targetIndex];
    if (targetIndex === -1 || !targetItem) {
        return null;
    }
    const diff = diffForRelative(relative);
    const adjacentItem = sortedItems[targetIndex + diff];
    return {
        targetItem,
        adjacentItem,
    };
}
/**
 * @param items to find bounds in reference to
 * @param sortOrders sortOrders to find bounds around to replace
 * If any sortOrders do not appear in items or sortOrders is empty, then returns null
 */
export function boundsToReplaceSortOrders(items, sortOrders) {
    if (sortOrders.length === 0) {
        return null;
    }
    const minSortOrder = Math.min(...sortOrders);
    const maxSortOrder = Math.max(...sortOrders);
    for (const sortOrder of sortOrders) {
        if (!items.find((i) => i.sortOrder === sortOrder)) {
            return null;
        }
    }
    let lo = undefined;
    let hi = undefined;
    for (const { sortOrder } of sortByOrder(items)) {
        if (sortOrder < minSortOrder) {
            lo = sortOrder;
        }
        else if (sortOrder > maxSortOrder) {
            hi = sortOrder;
            break;
        }
    }
    return [lo, hi];
}
/**
 * @param items to find bounds in reference to
 * @param target one sortOrder value in items to find bounds after
 */
export function boundsAfterTargetSortOrder(items, target) {
    const result = adjacentItemForTargetByFn(items, (i) => i.sortOrder, target, 'after');
    if (!result) {
        return [undefined, undefined];
    }
    return [result.targetItem.sortOrder, result.adjacentItem?.sortOrder];
}
/**
 * Find sortOrder bounds after the group containing or encompassing a specific sortOrder.
 *
 * This function:
 * 1. Finds the group whose range [min, max] contains the targetSortOrder
 * 2. Returns bounds after the maximum sortOrder in that group
 * 3. Returns null if the targetSortOrder does not fall within any group
 *
 * @example Exact match
 * const items = [
 *   { sortOrder: 0, groupId: 'A' },
 *   { sortOrder: 0.5, groupId: 'A' },
 *   { sortOrder: 1, groupId: 'B' },
 * ];
 * boundsAfterGroupContainingSortOrder(items, 0, 'groupId')
 * Returns [0.5, 1] - bounds after group A's maximum sortOrder
 *
 * @example Within group range
 * const items = [
 *   { sortOrder: 2, groupId: 'A' },
 *   { sortOrder: 2.33, groupId: 'A' },
 *   { sortOrder: 2.66, groupId: 'A' },
 *   { sortOrder: 5, groupId: 'B' },
 * ];
 * boundsAfterGroupContainingSortOrder(items, 2.5, 'groupId')
 * Returns [2.66, 5] - bounds after group A because 2.5 falls within [2, 2.66]
 */
export function boundsAfterGroupContainingSortOrder(items, targetSortOrder, groupIdKey) {
    // Build map of group ranges to check if targetSortOrder falls within any group
    const groupRanges = new Map();
    for (const item of items) {
        const groupId = item[groupIdKey];
        const existing = groupRanges.get(groupId);
        if (existing) {
            existing.min = Math.min(existing.min, item.sortOrder);
            existing.max = Math.max(existing.max, item.sortOrder);
        }
        else {
            groupRanges.set(groupId, {
                min: item.sortOrder,
                max: item.sortOrder,
                groupId,
            });
        }
    }
    // Find the group that contains the targetSortOrder (min <= target <= max)
    const containingGroupEntry = Array.from(groupRanges.values()).find((range) => targetSortOrder >= range.min && targetSortOrder <= range.max);
    if (!containingGroupEntry) {
        return null;
    }
    const maxGroupSortOrder = containingGroupEntry.max;
    // Find the next sortOrder after the group's maximum
    const sortedItems = sortByOrder(items);
    const nextItem = sortedItems.find(item => item.sortOrder > maxGroupSortOrder);
    return [maxGroupSortOrder, nextItem?.sortOrder];
}
export function sortOrdersWithinBounds([lo, hi], count) {
    if (lo !== undefined && hi !== undefined) {
        return Array.from({ length: count }, (_, i) => {
            return ((hi - lo) / (count + 1)) * (i + 1) + lo;
        });
    }
    else if (lo !== undefined) {
        return Array.from({ length: count }, (_, i) => {
            return i + 1 + lo;
        });
    }
    else if (hi !== undefined) {
        return Array.from({ length: count }, (_, i) => {
            return hi - count + i;
        });
    }
    else {
        return Array.from({ length: count }, (_, i) => i);
    }
}
export function sortByOrder(items) {
    return items.slice().sort((a, b) => a.sortOrder - b.sortOrder);
}
export function sortByFn(items, fn) {
    return items.slice().sort((a, b) => {
        const aVal = fn(a);
        const bVal = fn(b);
        if (aVal > bVal) {
            return 1;
        }
        else if (aVal < bVal) {
            return -1;
        }
        else {
            return 0;
        }
    });
}
/**
 * Convert a list of sortable items that has negative and decimal sort orders
 * to 1 through N sort orders.
 *
 * @return a map of the original sort order value to it's normalized value
 */
export function getNormalizedSortOrders(items) {
    const map = new Map();
    sortByOrder(items).forEach((item, index) => {
        map.set(item.sortOrder, index + 1);
    });
    return map;
}
//# sourceMappingURL=sort-order.js.map